<script setup lang="ts">
import { useVfm } from 'vue-final-modal'
import type {
  AccumulationInfo,
  BundledGoodsInfo,
  DeliveryPolicyInfo, EnterpriseInfo, GoodsBase, GoodsBrandMaster, GoodsContents,
  GoodsDetail,
  GoodsDetailResponse,
  GoodsPriceHistory, GoodsSafeCertiHistory, GoodsSpecInfo, LongTermInterestFreeInfo,
} from '~/types/goods/goods-detail'
import { useRecentGoods } from '~/composables/display/use-recent-goods'
import { ShopPopupTargetPageType } from '~/enums/shop-common-enum'
import { useSelectedItemStore } from '~/stores/goods/selected-item-store'
import { useHeadMeta } from '~/composables/use-head-meta'
import useAsyncDataStatusCheck from '~/composables/use-asyncdata-status'
import type { PromotionInfo } from '~/types/marketing/order-sheet-coupon-popup'
import { getGoodsDetail } from '~/services/goods/goods-service'

const screenId = 'SB-MO-PD-00100'
const pageTitleArray = ['띵샵', '상품', '상품상세']

const { generateGTMData, generatePageTitle } = useGAHandler()
const { setDefaultHeadMeta } = useHeadMeta()
const { currentTabIndex } = useTab()
const toTopFloationBtnSpace = ref()
const selectedItemStore = useSelectedItemStore()

/* init */
const goodsNo = computed(() => useRoute().query.goodsNo as string)
const previewDate = useRoute().query.previewDate as string
pageTitleArray.push(goodsNo.value as string)

const prGoodsBase = ref<GoodsBase>()
const promoList = ref<PromotionInfo[]>()
const prBrandMst = ref<GoodsBrandMaster>()
const prRelGoodsInfo = ref<BundledGoodsInfo[]>()
const prGoodsPrceHist = ref<GoodsPriceHistory[]>()
const etDeliPolcBase = ref<DeliveryPolicyInfo>()
const prGoodsItemInfo = ref<GoodsSpecInfo[]>()
const etEntrBase = ref<EnterpriseInfo>()
const qnATotalCount = ref()
const longTermInterestFreeInfo = ref<LongTermInterestFreeInfo>()
const goodsContentsDto = ref<GoodsContents>()
const accmResponse = ref<AccumulationInfo>()
const prGoodsSafeCertiHist = ref<GoodsSafeCertiHistory[]>()
const likeYn = ref()

const isBundleGoods = ref()

const thumbnailGoodsImageUrl = ref('')

const gtmEcommerce = ref()

const productInfoTabData = ref([])
const { data, status, error, refresh } = useLocaAsyncData<GoodsDetailResponse, GoodsDetail>
(`base:getGoodsDetail:${goodsNo.value}`, () => getGoodsDetail({ goodsNo: goodsNo.value, previewDate }), null, false)
const { isLoading } = useAsyncDataStatusCheck(status, { error })

function init() {
  prGoodsBase.value = data.value.prGoodsBase
  promoList.value = data.value.promoList
  prBrandMst.value = data.value.prBrandMst
  prRelGoodsInfo.value = data.value.prRelGoodsInfo
  prGoodsPrceHist.value = data.value.prGoodsPrceHist
  etDeliPolcBase.value = data.value.etDeliPolcBase
  prGoodsItemInfo.value = data.value.prGoodsItemInfo
  etEntrBase.value = data.value.etEntrBase
  qnATotalCount.value = data.value.qnATotalCount
  longTermInterestFreeInfo.value = data.value.longTermInterestFreeInfo
  goodsContentsDto.value = data.value.goodsContentsDto
  accmResponse.value = data.value.accmResponse
  prGoodsSafeCertiHist.value = data.value.prGoodsSafeCertiHist
  likeYn.value = data.value.likeYn

  const { isBundleGoods: isBundleGoodsFlag } = useGoodsBaseFlag(prGoodsBase.value)
  isBundleGoods.value = isBundleGoodsFlag

  thumbnailGoodsImageUrl.value = goodsContentsDto.value.goodsImageContentList.find(el => el.imgGbCd === 'P10')?.contFilePathNm
  setDefaultHeadMeta({
    metaTitle: prGoodsBase.value.goodsNm,
    metaImg: thumbnailGoodsImageUrl.value,
  })

  const maxCount = 999
  productInfoTabData.value = [
    {
      id: '1',
      title: '상품정보',
    },
    {
      id: '2',
      title: `리뷰 <span class="text-14 pl-1">0</span>`,
      updateTitle: function (count: number) {
        this.title = `리뷰 <span class="text-14 pl-1">${count > maxCount ? `${maxCount}+` : count}</span>`
      },
    },
    {
      id: '3',
      title: `Q&A <span class="text-14 pl-1">0</span>`,
      updateTitle: function (count: number) {
        this.title = `Q&A <span class="text-14 pl-1">${count > maxCount ? `${maxCount}+` : count}</span>`
      },
    },
  ]

  window.scrollTo(0, 0)

  const { setRecentGoods, setRecentGoodsList } = useRecentGoods()
  setRecentGoods(thumbnailGoodsImageUrl.value, prGoodsBase.value.goodsNm, goodsNo.value)
  setRecentGoodsList(thumbnailGoodsImageUrl.value, prGoodsBase.value.goodsNm, goodsNo.value)

  gtmEcommerce.value = generateGTMData(
    {
      ecommerce:
        {
          items: [
            {
              item_id: goodsNo.value,
              item_name: prGoodsBase.value.goodsNm,
              item_brand: prGoodsBase.value.brandNm,
              item_category: prGoodsBase.value.stdLrgCtgNo,
              item_category2: prGoodsBase.value.stdMidCtgNo,
              item_category3: prGoodsBase.value.stdSmlCtgNo,
              item_category4: prGoodsBase.value.stdThnCtgNo,
              price: prGoodsBase.value.salePrc,
              coupon_yn: prGoodsBase.value.showCouponDownloadButton ? 'Y' : 'N',
            },
          ],
        },
    },
  )
}

function onPurchase() {
  const { open: openCTAButtonModal } = useCTAButtonModal(goodsNo.value)
  openCTAButtonModal()
}

watch(data, () => {
  init()
  selectedItemStore.reset()
})

watch(() => useRoute().query.goodsNo, () => {
  refresh()
})

onBeforeRouteLeave((to, from, next) => {
  const { closeAll } = useVfm()
  closeAll()
  if (to.path !== '/order/sheet/') {
    selectedItemStore.reset()
  }
  next()
})

/* Other */
useMarketingPopup(ShopPopupTargetPageType.GOODS_DETAIL)

// 상품정보 탭 클릭시 스크롤 이동 테스트
const { y } = useWindowScroll()
const stickyTabClick = () => {
  y.value = window.scrollY
}
const showLineBanner = ref(false)
</script>

<template>
  <!-- [DEV] 240621 - header 수정 -->
  <PageContainer
    :footer="{ isShowMoreInfo: false }"
    :home="true"
    :menu="true"
    :cart="true"
    data-gtm-page
    :data-gtm-body="generateGTMData({ page_title: generatePageTitle(pageTitleArray), screen_id: screenId })"
  >
    <ShopSkeleton
      v-if="isLoading"
      type="product-detail"
    />
    <template v-else-if="!isLoading && !!prGoodsBase">
      <div
        data-gtm-view-item
        :data-gtm-ecommerce="gtmEcommerce"
      >
        <GoodsDetailGoodsSummary
          v-if="!isBundleGoods"
          :goods-no="goodsNo"
          :pr-goods-base="prGoodsBase"
          :et-entr-base="etEntrBase"
          :promo-list="promoList"
          :pr-brand-mst="prBrandMst"
          :pr-goods-prce-hist="prGoodsPrceHist"
          :long-term-interest-free-info="longTermInterestFreeInfo"
          :et-deli-polc-base="etDeliPolcBase"
          :goods-contents-dto="goodsContentsDto"
          :accm-response="accmResponse"
          :thumbnail-goods-image-url="thumbnailGoodsImageUrl"
        />
        <GoodsDetailBundleGoodsSummary
          v-else
          :pr-goods-base="prGoodsBase"
          :et-entr-base="etEntrBase"
          :pr-brand-mst="prBrandMst"
          :pr-goods-prce-hist="prGoodsPrceHist"
          :long-term-interest-free-info="longTermInterestFreeInfo"
          :goods-contents-dto="goodsContentsDto"
          :thumbnail-goods-image-url="thumbnailGoodsImageUrl"
        />

        <template v-if="isBundleGoods">
          <div
            v-if="showLineBanner"
            class="divider-thick bg-warm-gray-30"
          />
          <DisplaySubLineBanner
            :page-type="ShopPopupTargetPageType.GOODS_DETAIL"
            @show-line-banner="showLineBanner = true"
          />
        </template>

        <!-- [S] Tab 영역 -->
        <div class="h-auto">
          <div class="divider-thick bg-warm-gray-30" />
          <!-- 하단 sticky start 지점 -->
          <!-- [S] Tab Buttons -->
          <div class="sticky top-[var(--sticky-top-gap)] z-10 bg-background-white">
            <ShopTabButtons
              v-model="currentTabIndex"
              :items="productInfoTabData"
              type="text-sm-full"
              @tab-click="stickyTabClick"
            />
          </div>
          <!-- [E] Tab Buttons -->

          <div v-show="currentTabIndex === '1'">
            <GoodsDetailGoodsInfo
              :goods-no="goodsNo"
              :pr-goods-base="prGoodsBase"
              :pr-rel-goods-info="prRelGoodsInfo"
              :pr-goods-item-info="prGoodsItemInfo"
              :et-entr-base="etEntrBase"
              :et-deli-polc-base="etDeliPolcBase"
              :goods-contents-dto="goodsContentsDto"
              :pr-goods-safe-certi-hist="prGoodsSafeCertiHist"
            />
          </div>

          <div v-show="currentTabIndex === '2'">
            <GoodsDetailReview
              v-model="productInfoTabData[1]"
              :goods-no="!isBundleGoods ? goodsNo : prGoodsBase.repGoodsNo"
              :bundle-goods-no="isBundleGoods ? goodsNo : undefined"
              :pr-rel-goods-info="prRelGoodsInfo"
            />
          </div>

          <div v-show="currentTabIndex === '3'">
            <GoodsDetailQnA
              v-model="productInfoTabData[2]"
              :goods-no="!isBundleGoods ? goodsNo : prGoodsBase.repGoodsNo"
              :bundle-goods-no="isBundleGoods ? goodsNo : undefined"
              :pr-rel-goods-info="prRelGoodsInfo"
            />
          </div>
        </div>
        <!-- [E] Tab 영역 -->
      </div>

      <!-- [E] 개요 영역 -->
      <!-- Fixed bottom -->
      <ClientOnly>
        <Teleport to="#fixedBottom">
          <GoodsDetailCTAButtonLayer
            :goods-no="goodsNo"
            :pr-goods-base="prGoodsBase"
            :like-yn="likeYn"
            @purchase="onPurchase"
            @is-stretch="() => toTopFloationBtnSpace = 138"
          />
        </Teleport>
      </ClientOnly>
      <DisplayFloatingBanner
        :page-type="ShopPopupTargetPageType.GOODS_DETAIL"
        :space="prGoodsBase.saleMethCd === '10' ? 104 : 138"
      />
      <ShopFloatingActionButtons :space="toTopFloationBtnSpace" />
    </template>
  </PageContainer>
</template>

<style scoped>

</style>
